import { mapEnum } from "@/utils/strings";
import {
  PEOPLE_VIEW_PARAM,
  PeopleEntityView,
  PersonColumn,
  PersonData,
  PersonReferenceabilityLevel,
  getPersonFixedOptions
} from "./types";
import { ScoreStrength } from "@/components/controls/ScoreBars";
import { getCurrentLocale } from "@/utils/locale";

export const peopleColumnKeys = Object.values(PersonColumn);

// Whether the param already names one of the two views.
export const isPeopleViewParam = (
  viewParam: string | null
): viewParam is PeopleEntityView =>
  viewParam === PeopleEntityView.COMPANY ||
  viewParam === PeopleEntityView.PARTNER;

export const withPeopleViewParam = (
  currentParams: URLSearchParams,
  value: string
): URLSearchParams => {
  const nextParams = new URLSearchParams(currentParams);
  nextParams.set(PEOPLE_VIEW_PARAM, value);
  return nextParams;
};

export const referenceabilityLevels = mapEnum(
  PersonReferenceabilityLevel,
  (value) => value
);

export const referenceabilityLevelsToScoreBarsMap: {
  [key: number]: ScoreStrength | undefined;
} = {
  [PersonReferenceabilityLevel.UNKNOWN]: undefined,
  [PersonReferenceabilityLevel.UNLIKELY_TO_ENDORSE]: "low",
  [PersonReferenceabilityLevel.WEAK_ADVOCATE]: "medium",
  [PersonReferenceabilityLevel.STRONG_ADVOCATE]: "high"
};

export const getFullName = (
  person: Partial<PersonData>,
  locale: string = getCurrentLocale()
) => {
  const { firstName, lastName } = person;

  if (!firstName && !lastName) return "";

  const cleanFirstName = firstName?.trim().replace(/\s+/g, " ") || "";
  const cleanLastName = lastName?.trim().replace(/\s+/g, " ") || "";

  const [primaryName, secondaryName] =
    new Intl.Collator(locale).compare("a", "b") < 0
      ? [cleanFirstName, cleanLastName]
      : [cleanLastName, cleanFirstName];

  return [primaryName, secondaryName]
    .filter(Boolean)
    .join(" ")
    .trim()
    .replace(/\s+/g, " ");
};

export const getSortableName = (
  person: PersonData,
  locale: string = getCurrentLocale()
) => {
  const fullName = getFullName(person, locale);
  return fullName.toLocaleLowerCase(locale);
};

export const getSpecialPersonDisplayValue = (
  value: string | null | undefined,
  t: (key: string) => string
): string | null => {
  if (value == null) return null;

  const fixedOptions = getPersonFixedOptions(t);
  const matchingOption = fixedOptions.find((option) => option.id === value);
  return matchingOption?.displayName ?? null;
};
